// White-label public branding for pre-auth / token pages (Tranche 5).
//
// The signed-in app shell gets branding via BrandingContext (GET
// /tenants/current). But public pages — the candidate portal, careers site,
// check-in rating, apply form — render BEFORE any login, so they can't use
// that. They resolve branding by HOSTNAME instead: a tenant that registers
// its domains gets its name/logo on those pages; every other host (incl. the
// platform's own domains) falls through to the deployment defaults below.
//
// Same endpoint the sign-in screen uses (see App.tsx useLoginBranding).
// Deliberately unauthenticated — plain fetch, no MSAL header.

import { useEffect, useState } from 'react';
import { PUBLIC_API_BASE } from './api';

export interface PublicBranding {
  app_name: string;
  company_name: string;
  tagline: string;
  logo_url: string | null;
  primary_color: string | null;
  support_email: string | null;
  ghl_chat_location_id: string | null;
  ghl_chat_widget_id: string | null;
}

// [split W1-B] Neutral, tenant-agnostic placeholder for the BACKEND's unset-tenant
// case (mirrors services/tenantBranding.ts NEUTRAL_APP_NAME). Exported for any
// caller that genuinely wants a tenant-agnostic label. NOT used as this
// deployment's loading/failure default — see PUBLIC_BRANDING_DEFAULTS below,
// which stays the FOUNDING (FNS) identity to keep FNS byte-identical.
export const NEUTRAL_APP_NAME = 'Staffing Portal';

// Deployment defaults for an UNMATCHED host, shown while /public/branding is in
// flight and if it can't be reached. These are the FOUNDING tenant's identity
// (env-overridable via VITE_APP_NAME / VITE_COMPANY_NAME for a reseller's own
// frontend build), so FNS's single-domain deployment renders "FNS AI" during
// load and on failure EXACTLY as it did pre-split — byte-identical. A tenant
// custom domain still resolves its real name by host; the platform's own host
// resolves the platform name server-side (is_platform). Reverting these to a
// neutral literal would flash "Staffing Portal" on FNS's live sign-in — a
// byte-identity regression, so keep them founding-default.
export const PUBLIC_BRANDING_DEFAULTS: PublicBranding = {
  app_name: (import.meta.env.VITE_APP_NAME as string | undefined)?.trim() || 'Boaz AI',
  company_name: (import.meta.env.VITE_COMPANY_NAME as string | undefined)?.trim() || 'Boaz AI',
  tagline: '',
  logo_url: null,
  primary_color: null,
  support_email: (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined)?.trim() || 'support@boazaiengine.com',
  ghl_chat_location_id: null,
  ghl_chat_widget_id: null,
};

// Careers contact address shown on the public careers pages. Env-overridable
// so a white-label tenant points applicants at their own inbox.
export const CAREERS_EMAIL =
  (import.meta.env.VITE_CAREERS_EMAIL as string | undefined)?.trim()
  || (import.meta.env.VITE_CONTACT_EMAIL as string | undefined)?.trim()
  || 'connect@boazaiengine.com';

/**
 * Resolve public branding for the current hostname. Returns the deployment
 * defaults immediately, then swaps in the tenant's branding if this host is
 * registered to one. Safe on every public page — a failed/unmatched lookup
 * just keeps the defaults.
 */
export function usePublicBranding(): PublicBranding {
  const [branding, setBranding] = useState<PublicBranding>(PUBLIC_BRANDING_DEFAULTS);

  useEffect(() => {
    let cancelled = false;
    const host = encodeURIComponent(window.location.hostname);
    fetch(`${PUBLIC_API_BASE}/public/branding?host=${host}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d?.matched || !d.branding) return;
        const b = d.branding as Partial<PublicBranding>;
        setBranding({
          app_name: b.app_name || PUBLIC_BRANDING_DEFAULTS.app_name,
          company_name: b.company_name || PUBLIC_BRANDING_DEFAULTS.company_name,
          tagline: b.tagline ?? '',
          logo_url: b.logo_url ?? null,
          primary_color: b.primary_color ?? null,
          support_email: b.support_email ?? PUBLIC_BRANDING_DEFAULTS.support_email,
          ghl_chat_location_id: b.ghl_chat_location_id ?? PUBLIC_BRANDING_DEFAULTS.ghl_chat_location_id,
          ghl_chat_widget_id: b.ghl_chat_widget_id ?? PUBLIC_BRANDING_DEFAULTS.ghl_chat_widget_id,
        });
      })
      .catch(() => { /* defaults are always a safe render */ });
    return () => { cancelled = true; };
  }, []);

  return branding;
}
